import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["playlistId", "playlistItem", "position", "playlistLength", "cardModel", "isLoadingCardModel"];
import I18n from 'I18n';
import { useState } from 'react';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { getPlaylistItemStatus } from '../playlistItemsUtils';
import { usePlaylistItemManager } from './PlaylistItemManager';
import { useCanUserEditPlaylist } from 'academy-common-lib/features/savedItems/savedItemsHooks';
import { usePlaylist } from '../../savedItems/savedItemsQueries.migrated';
import { useIsPartner } from '../../partners';
import { useHasGate } from '../../accounts';
import { RemoveFromHubspotPlaylistModal } from './RemoveFromHubspotPlaylistModal';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import LearningContentCard from 'academy-common-lib/features/learningContentCard/components/LearningContentCard';
import ReducedCardSkeleton from 'academy-common-lib/components/Skeletons/ReducedCardSkeleton';
import { ACStep } from 'academy-common-lib/components/progress/ACStep';
import { Box } from 'foundations-components/transitional/components/Box';
import { Flex } from 'foundations-components/components/Flex';
import { Spacer } from 'foundations-components/components/Spacer';
import { DragHandleIcon } from 'foundations-assets/icons/components/DragHandleIcon';
import { Text } from 'foundations-components/components/Text';
import ACInlineContentCard from 'academy-common-lib/components/card/ACInlineContentCard';
import { ACContentCardThumbnailIcon } from 'academy-common-lib/components/card/ACContentCardThumbnailIcon';
import CardMenuIcon from 'academy-common-lib/features/cardMenu/components/CardMenuIcon';
import { SideNavItem } from 'foundations-components/transitional/components/SideNav';
import { HR } from 'foundations-components/transitional/components/HR';
import { MenuRow } from 'academy-common-lib/features/cardMenu/components/MobileCardMenu';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Link } from 'foundations-components/transitional/components/Link';
import { isInternalContent } from 'academy-common-lib/features/learningContent/utils';
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const LIST_ID = 'PLAYLIST_ITEM_LIST';
export const PlaylistDragProvider = ({
  children
}) => {
  const {
    movePlaylistItem
  } = usePlaylistItemManager();
  const onDragEnd = result => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }
    // dropped in the same position
    if (result.destination.index === result.source.index) {
      return;
    }
    movePlaylistItem(result.source.index, result.destination.index);
  };
  return /*#__PURE__*/_jsx(DragDropContext, {
    onDragEnd: onDragEnd,
    children: /*#__PURE__*/_jsx(Droppable, {
      droppableId: LIST_ID,
      children: dropProvided => /*#__PURE__*/_jsxs("div", {
        ref: dropProvided.innerRef,
        children: [children, dropProvided.placeholder]
      })
    })
  });
};
const hasAccessToPlaylistItem = (playlistItem, isPartner, hasTrainerAccessGate, cardModel) => {
  if (isInternalContent(playlistItem) && !cardModel) {
    return false;
  }
  return playlistItem.visibility === 'PUBLIC' || isInternalContent(playlistItem) || playlistItem.visibility === 'PARTNER' && isPartner || playlistItem.visibility === 'PARTNER_TYPES_SALES' && isPartner || playlistItem.visibility === 'TRAINER' && hasTrainerAccessGate;
};
export const PlaylistItem = _ref => {
  let {
      playlistId,
      playlistItem,
      position,
      playlistLength,
      cardModel,
      isLoadingCardModel
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    isMobile
  } = useMediaQuery();
  const userCanEditPlaylist = useCanUserEditPlaylist(playlistId);
  const {
    removePlaylistItem
  } = usePlaylistItemManager();
  const {
    playlist
  } = usePlaylist(playlistId);
  const isPartner = useIsPartner();
  const {
    hasGate: hasTrainerAccessGate
  } = useHasGate('Academy:HubSpotTrainerAccess');
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  if (!cardModel && playlistItem.visibility === 'PUBLIC') return null;
  const status = getPlaylistItemStatus(cardModel);
  const accessDenied = !hasAccessToPlaylistItem(playlistItem, isPartner, hasTrainerAccessGate, cardModel);
  const dragId = `${playlistItem.itemId}-${playlistItem.type.toString()}`;
  const isActiveHubspotPlaylist = (playlist === null || playlist === void 0 ? void 0 : playlist.type) === 'LEARNING_PATH' && (playlist === null || playlist === void 0 ? void 0 : playlist.active) === true;
  const removeFromPlaylist = () => {
    removePlaylistItem({
      itemId: playlistItem.itemId,
      type: playlistItem.type,
      language: playlistItem.language
    });
  };
  const handleRemoveClick = () => {
    if (isActiveHubspotPlaylist) {
      setIsRemoveModalOpen(true);
    } else {
      removeFromPlaylist();
    }
  };
  const renderRemoveFromPlaylistOption = setIsOpen => {
    const onClick = () => {
      handleRemoveClick();
      setIsOpen(false);
    };
    if (isMobile) {
      return /*#__PURE__*/_jsx(MenuRow, {
        children: /*#__PURE__*/_jsx(Link, {
          onClick: onClick,
          use: "unstyled",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.savedItems.playlistDetails.removeFromPlaylist"
          })
        })
      });
    }
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(HR, {
        distance: "extra-small"
      }), /*#__PURE__*/_jsx(SideNavItem, {
        onClick: onClick,
        role: "navigation-item",
        title: I18n.text('learningCenter.savedItems.playlistDetails.removeFromPlaylist')
      })]
    });
  };
  return /*#__PURE__*/_jsx(Draggable, {
    draggableId: dragId,
    index: position,
    isDragDisabled: !userCanEditPlaylist,
    children: dragProvided => {
      var _cardModel$progress;
      return /*#__PURE__*/_jsxs("div", Object.assign({
        "data-test-id": "playlist-item",
        "data-test-item-id": playlistItem.itemId,
        "data-test-item-type": playlistItem.type,
        ref: dragProvided.innerRef,
        style: Object.assign({}, dragProvided.draggableProps.style)
      }, props, dragProvided.draggableProps, dragProvided.dragHandleProps, {
        children: [/*#__PURE__*/_jsx("div", {
          style: playlistItem.hasBeenRemoved ? {
            visibility: 'hidden',
            overflow: 'hidden',
            height: '0'
          } : undefined,
          children: /*#__PURE__*/_jsxs(Flex, {
            children: [/*#__PURE__*/_jsx(ACStep, {
              completed: status === 'COMPLETED',
              connectorActive: status === 'COMPLETED',
              iconActive: status === 'IN_PROGRESS',
              iconOffset: "0",
              showConnector: position < playlistLength - 1
            }), /*#__PURE__*/_jsx(Spacer, {
              marginInlineEnd: "300"
            }), userCanEditPlaylist && /*#__PURE__*/_jsx(Flex, {
              flexDirection: "column",
              justifyContent: "center",
              marginInlineEnd: "300",
              children: /*#__PURE__*/_jsx("div", {
                style: {
                  cursor: 'move'
                },
                children: /*#__PURE__*/_jsx(Text, {
                  color: "text-core-subtle",
                  children: /*#__PURE__*/_jsx(DragHandleIcon, {
                    size: "600"
                  })
                })
              })
            }), /*#__PURE__*/_jsx(Box, {
              grow: 1,
              paddingBlockEnd: "500",
              children: isLoadingCardModel ? /*#__PURE__*/_jsx(ReducedCardSkeleton, {
                size: isMobile ? 'small' : 'medium'
              }) : accessDenied ? /*#__PURE__*/_jsx(ACInlineContentCard, {
                description: isMobile ? undefined : I18n.text('learningCenter.savedItems.playlistDetails.playlistItemAccessDeniedDescription'),
                name: I18n.text('learningCenter.savedItems.playlistDetails.playlistItemAccessDeniedTitle'),
                size: isMobile ? 'extra-small' : 'medium',
                thumbnailOverlay: /*#__PURE__*/_jsx(ACContentCardThumbnailIcon, {
                  pictogram: "AccountSecurity"
                }),
                truncateDescription: false
              }) : cardModel && /*#__PURE__*/_jsx(LearningContentCard, {
                body: !isMobile && cardModel.description,
                item: cardModel,
                menuAlwaysVisible: true,
                overviewType: "playlist",
                playlistId: playlistId,
                position: position,
                renderMenu: () => /*#__PURE__*/_jsx(CardMenuIcon, {
                  item: cardModel,
                  renderExtraOptions: userCanEditPlaylist ? setIsOpen => renderRemoveFromPlaylistOption(setIsOpen) : undefined
                }),
                size: isMobile ? 'small' : 'medium',
                thumbnailUse: (cardModel === null || cardModel === void 0 || (_cardModel$progress = cardModel.progress) === null || _cardModel$progress === void 0 ? void 0 : _cardModel$progress.status) === 'COMPLETED' ? 'dark' : 'default'
              })
            })]
          })
        }), isRemoveModalOpen && /*#__PURE__*/_jsx(RemoveFromHubspotPlaylistModal, {
          onClose: () => setIsRemoveModalOpen(false),
          onConfirm: () => {
            removeFromPlaylist();
            setIsRemoveModalOpen(false);
          }
        })]
      }));
    }
  }, dragId);
};